/* eslint-disable @typescript-eslint/no-explicit-any */
import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { LayoutColumns2 } from '@juvare/common-web-ui/LayoutColumns2';
import { LayoutRows2 } from '@juvare/common-web-ui/LayoutRows2';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, Button, Grid, IconButton } from '@mui/material';
import React from 'react';
import { formatWidgetConfig } from '../formatWidgetConfig';
import { showJsonEditorDialog } from '../showJsonEditorDialog';
import { useRootWidget } from '../useRootWidget';
import { WidgetComponent } from '../WidgetComponent';
import { useWidgetEditorContext, WidgetEditorContext } from '../WidgetEditorContext';
import { WidgetEditorToolbar } from '../WidgetEditorToolbar';
/**
 * Fully featured drag and drop widget editor.
 */
export const WidgetEditor = (props) => {
    var _a, _b;
    const { onSave, onCancel, onChanged, initialPreviewState = false } = props;
    const rootElement = React.useRef(null);
    const [preview, setPreview] = React.useState(initialPreviewState);
    const maxUndoStackLength = (_a = props.maxUndoStackLength) !== null && _a !== void 0 ? _a : 0;
    const handleRootConfigChange = React.useCallback((newRootConfig) => {
        setUndoStack((prevUndoStack) => {
            const pointer = prevUndoStack.pointer + 1;
            const newUndoStack = { stack: [...prevUndoStack.stack], pointer: pointer };
            newUndoStack.stack.splice(pointer);
            newUndoStack.stack.push(newRootConfig);
            if (newUndoStack.stack.length > maxUndoStackLength) {
                newUndoStack.stack.splice(0, 1);
                newUndoStack.pointer--;
            }
            return newUndoStack;
        });
        if (onChanged) {
            onChanged(true, () => onSave(newRootConfig));
        }
    }, [maxUndoStackLength, onSave, onChanged]);
    const [rootConfig, setRootConfig, context] = useWidgetEditorContext(!preview, rootElement, props.config, handleRootConfigChange, props.widgets);
    const [undoStack, setUndoStack] = React.useState({ stack: [props.config], pointer: 0 });
    const handleUndo = React.useCallback(() => {
        setUndoStack((prevUndoStack) => {
            const pointer = prevUndoStack.pointer - 1;
            if (pointer >= 0) {
                const newRootConfig = prevUndoStack.stack[pointer];
                setRootConfig(newRootConfig);
                if (onChanged) {
                    onChanged(pointer > 0, () => onSave(newRootConfig));
                }
                return { stack: prevUndoStack.stack, pointer: pointer };
            }
            else {
                return prevUndoStack;
            }
        });
    }, [setRootConfig, onSave, onChanged]);
    const handleRedo = React.useCallback(() => {
        setUndoStack((prevUndoStack) => {
            const pointer = prevUndoStack.pointer + 1;
            if (pointer < prevUndoStack.stack.length) {
                const newRootConfig = prevUndoStack.stack[pointer];
                setRootConfig(newRootConfig);
                if (onChanged) {
                    onChanged(pointer > 0, () => onSave(newRootConfig));
                }
                return { stack: prevUndoStack.stack, pointer: pointer };
            }
            else {
                return prevUndoStack;
            }
        });
    }, [setRootConfig, onSave, onChanged]);
    const [widgetData, setWidgetData] = React.useState();
    const [root, key] = useRootWidget((_b = props.rootParams) !== null && _b !== void 0 ? _b : defaultRootParams, props.widgets, !preview, rootConfig, initialData, setWidgetData);
    const toolbarButtons = props.toolbarButtons &&
        props.toolbarButtons.map((button) => (React.createElement(Button, { key: button.label, sx: { ml: 2 }, variant: 'outlined', color: 'primary', onClick: () => button.onClick(rootConfig, (newRootConfig) => {
                setRootConfig(newRootConfig);
                handleRootConfigChange(newRootConfig);
            }) }, button.label)));
    const canUndo = undoStack.pointer > 0;
    const canRedo = undoStack.pointer < undoStack.stack.length - 1;
    return (React.createElement(Box, { ref: rootElement, sx: { width: '100%', height: '100%' } }, context && (React.createElement(WidgetEditorContext.Provider, { value: context },
        React.createElement(LayoutRows2, { row1: preview ? (React.createElement(Grid, { container: true, mb: 2 },
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(Button, { variant: 'contained', color: 'primary', onClick: () => setPreview(false) }, "Close Preview"),
                    toolbarButtons,
                    React.createElement(Button, { sx: { ml: 2 }, variant: 'outlined', color: 'primary', onClick: () => showAlertDialog({
                            title: 'Data JSON',
                            content: React.createElement("pre", { style: { margin: 0 } }, JSON.stringify(widgetData, undefined, '  ')),
                            maxWidth: 'lg',
                        }) }, "Show Data JSON")))) : (React.createElement(Box, { mb: 2, sx: { display: 'grid', gridTemplateColumns: '1fr auto 1fr' } },
                React.createElement(Box, { sx: { whiteSpace: 'nowrap' } },
                    React.createElement(Button, { variant: 'outlined', color: 'primary', onClick: () => setPreview(true) }, "Preview"),
                    toolbarButtons,
                    maxUndoStackLength > 0 && (React.createElement(React.Fragment, null,
                        React.createElement(IconButton, { color: canUndo ? 'primary' : 'default', disabled: !canUndo, sx: { ml: 2 }, onClick: handleUndo },
                            React.createElement(UndoIcon, null)),
                        React.createElement(IconButton, { color: canRedo ? 'primary' : 'default', disabled: !canRedo, onClick: handleRedo },
                            React.createElement(RedoIcon, null))))),
                React.createElement(Box, { sx: { whiteSpace: 'nowrap', pl: 2, pr: 2 } },
                    onCancel && (React.createElement(Button, { variant: 'outlined', color: 'primary', sx: { mr: 2 }, onClick: () => onCancel() }, "Cancel")),
                    React.createElement(Button, { variant: 'contained', color: 'primary', onClick: () => {
                            if (props.formatConfigOnSave) {
                                onSave(formatWidgetConfig(props.widgets, rootConfig));
                            }
                            else {
                                onSave(rootConfig);
                            }
                        } }, "Save")),
                React.createElement(Box, { sx: { whiteSpace: 'nowrap', textAlign: 'right' } }, props.allowEditConfigJson && (React.createElement(Button, { variant: 'outlined', color: 'primary', onClick: () => {
                        showJsonEditorDialog({
                            title: 'Config JSON',
                            json: props.formatConfigOnSave
                                ? formatWidgetConfig(props.widgets, rootConfig)
                                : rootConfig,
                            onValidate: (json) => new Promise((resolve, reject) => {
                                if (json == null) {
                                    reject(new Error('JSON cannot be null, expected object'));
                                }
                                else if (Array.isArray(json)) {
                                    reject(new Error('JSON cannot be array, expected object'));
                                }
                                else if (typeof json !== 'object') {
                                    reject(new Error('JSON cannot be primitive value, expected object'));
                                }
                                else {
                                    resolve(json);
                                }
                            }),
                            onSave: (json) => {
                                handleRootConfigChange(json);
                                setRootConfig(json);
                            },
                        });
                    } }, "Edit JSON"))))), row2: preview ? (React.createElement(Box, { pt: 1, sx: { width: '100%', height: '100%', boxSizing: 'border-box', overflow: 'auto' } }, root && React.createElement(WidgetComponent, Object.assign({ key: key }, root)))) : (React.createElement(LayoutColumns2, { column1: React.createElement(WidgetEditorToolbar, Object.assign({}, props)), column2: React.createElement(Box, { pt: 4, pr: 2, pb: 8, pl: 3, sx: { width: '100%', height: '100%', boxSizing: 'border-box', overflow: 'auto' } }, root ? React.createElement(WidgetComponent, Object.assign({ key: key }, root)) : React.createElement("div", null)), fullWidthColumn: 'column2' })), fullHeightRow: 'row2' })))));
};
const defaultRootParams = {};
const initialData = {};
